import React, { useRef, useState, useEffect } from "react";
import { Upload, Trash2, Music2, ListMusic, Loader2, Cloud } from "lucide-react";
import { toast } from "sonner";
import { useStudio } from "@/studio/StudioContext";
import { useAuth } from "@/auth/AuthContext";

export default function Crate() {
  const { crate, addToCrate, removeFromCrate, clearCrate, loadToDeck, loadCloudCrate } = useStudio();
  const { user } = useAuth();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const signedIn = !!(user && user.id);

  // Pull the user's saved tracks from the cloud when signed in.
  useEffect(() => {
    if (signedIn) loadCloudCrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  const onFiles = async (files) => {
    const n = await addToCrate(files);
    if (n) {
      toast.success(`Added ${n} track${n === 1 ? "" : "s"} to the crate`);
      if (!signedIn) toast.info("Sign in to save your tracks so they're here next time.");
    } else {
      toast.error("Please add audio files (MP3, WAV, OGG…)");
    }
  };

  const loadDeck = async (deckId, item) => {
    try {
      await loadToDeck(deckId, item);
      toast.success(`Cued "${item.name.slice(0, 22)}" on ${deckId === "deckA" ? "Deck A" : "Deck B"}`);
    } catch (_) {
      toast.error("Could not load this track");
    }
  };

  return (
    <div className="flex flex-col min-h-0" data-testid="crate">
      <div className="flex items-center justify-between mb-2">
        <span className="hw-label flex items-center gap-1.5">
          <ListMusic size={13} className="text-[#00e5ff]" /> Crate — your set list
          {signedIn && (
            <span className="flex items-center gap-1 text-[9px] text-[#00e5ff]/70" title="Your tracks are saved to your account">
              <Cloud size={11} /> synced
            </span>
          )}
        </span>
        <div className="flex items-center gap-2">
          <button
            data-testid="crate-add-btn"
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-[#404040] text-[10px] hw-label hover:bg-[#242424] transition-colors"
          >
            <Upload size={12} /> Add Tracks
          </button>
          {crate.length > 0 && (
            <button
              data-testid="crate-clear-btn"
              onClick={clearCrate}
              className="px-2.5 py-1.5 rounded-sm border border-[#333] text-[10px] hw-label text-neutral-500 hover:text-[#ff3333] hover:border-[#ff3333] transition-colors"
            >
              Clear
            </button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="audio/*"
            multiple
            className="hidden"
            data-testid="crate-file-input"
            onChange={(e) => {
              onFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          onFiles(e.dataTransfer.files);
        }}
        className="flex-1 min-h-0 rounded-md border transition-colors overflow-y-auto"
        style={{
          borderColor: dragOver ? "#00e5ff" : "#262626",
          background: dragOver ? "rgba(0,229,255,0.06)" : "#0a0a0a",
          maxHeight: 260,
        }}
      >
        {crate.length === 0 ? (
          <div className="h-full min-h-[120px] flex flex-col items-center justify-center gap-2 text-center py-8 px-4">
            <Music2 size={26} className="text-neutral-700" />
            <p className="text-neutral-500 text-sm">Drop your tracks here or tap <span className="text-[#00e5ff]">Add Tracks</span></p>
            <p className="text-neutral-700 text-[11px]">
              {signedIn
                ? "Your uploads are saved to your account and stay here across sessions."
                : "Sign in to keep your tracks saved across sessions."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-[#161616]">
            {crate.map((item, i) => (
              <div
                key={item.id}
                data-testid={`crate-item-${i}`}
                className="flex items-center gap-2 px-3 py-2 hover:bg-[#111] transition-colors"
              >
                <span className="font-mono-data text-[10px] text-neutral-600 w-5 shrink-0">{i + 1}</span>
                {item.uploading ? (
                  <Loader2 size={13} className="text-[#00e5ff] shrink-0 animate-spin" />
                ) : (
                  <Music2 size={13} className="text-neutral-500 shrink-0" />
                )}
                <span className="flex-1 min-w-0 truncate text-sm text-neutral-200">{item.name}</span>
                <button
                  data-testid={`crate-load-a-${i}`}
                  onClick={() => loadDeck("deckA", item)}
                  title="Load this track onto Deck A (left turntable)"
                  className="shrink-0 px-2.5 py-1 rounded-sm text-[10px] font-bold transition-colors hover:bg-[#00e5ff1a]"
                  style={{ border: "1px solid #00e5ff55", color: "#00e5ff" }}
                >
                  → A
                </button>
                <button
                  data-testid={`crate-load-b-${i}`}
                  onClick={() => loadDeck("deckB", item)}
                  title="Load this track onto Deck B (right turntable)"
                  className="shrink-0 px-2.5 py-1 rounded-sm text-[10px] font-bold transition-colors hover:bg-[#ff007f1a]"
                  style={{ border: "1px solid #ff007f55", color: "#ff007f" }}
                >
                  → B
                </button>
                <button
                  data-testid={`crate-remove-${i}`}
                  onClick={() => removeFromCrate(item.id)}
                  className="shrink-0 w-7 h-7 rounded-sm flex items-center justify-center text-neutral-600 hover:text-[#ff3333] transition-colors"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
